// pages/SalesforceAttachments/SalesforceAttachments.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Download,
  Trash2,
  Eye,
  MoreVertical,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  File as FileIcon,
  Grid,
  List,
  SortAsc,
  SortDesc,
  Calendar,
  User,
  ChevronDown,
  X as CloseIcon,
  Loader,
  AlertCircle,
  ExternalLink,
  Link as LinkIcon,
  Building2,
  Users,
  Briefcase,
  FileQuestion,
  FolderKanban,
  UserCircle,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const SalesforceAttachments = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterParentType, setFilterParentType] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState(new Set());
  const [stats, setStats] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAttachments, setTotalAttachments] = useState(0);
  const limit = 24;

  useEffect(() => {
    fetchAttachments();
    fetchStats();
  }, [page, sortBy, sortOrder, searchQuery, filterParentType]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllSalesforceAttachments({
        page,
        limit,
        sortBy,
        sortOrder,
        search: searchQuery,
        parentType: filterParentType !== "all" ? filterParentType : undefined,
      });

      const attachmentsArray = response.data?.data || response.data || [];
      setAttachments(attachmentsArray);
      setTotalPages(response.data?.pagination?.totalPages || 1);
      setTotalAttachments(
        response.data?.pagination?.total || attachmentsArray.length,
      );
    } catch (error) {
      console.error("Error fetching attachments:", error);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getSalesforceAttachmentStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Are you sure you want to delete this attachment?"))
      return;

    try {
      await apiService.deleteSalesforceAttachment(attachmentId);
      fetchAttachments();
      selectedAttachments.delete(attachmentId);
      setSelectedAttachments(new Set(selectedAttachments));
    } catch (error) {
      console.error("Error deleting attachment:", error);
      alert("Failed to delete attachment");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAttachments.size === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedAttachments.size} attachment(s)?`,
      )
    )
      return;

    try {
      await Promise.all(
        Array.from(selectedAttachments).map((attachmentId) =>
          apiService.deleteSalesforceAttachment(attachmentId),
        ),
      );
      fetchAttachments();
      setSelectedAttachments(new Set());
    } catch (error) {
      console.error("Error deleting attachments:", error);
      alert("Failed to delete some attachments");
    }
  };

  const toggleAttachmentSelection = (attachmentId) => {
    const newSelection = new Set(selectedAttachments);
    if (newSelection.has(attachmentId)) {
      newSelection.delete(attachmentId);
    } else {
      newSelection.add(attachmentId);
    }
    setSelectedAttachments(newSelection);
  };

  const selectAllAttachments = () => {
    if (selectedAttachments.size === attachments.length) {
      setSelectedAttachments(new Set());
    } else {
      setSelectedAttachments(new Set(attachments.map((a) => a.id)));
    }
  };

  const getFullFileUrl = (s3Url) => {
    if (!s3Url) return null;
    if (s3Url.startsWith("http://") || s3Url.startsWith("https://")) {
      return s3Url;
    }
    const S3_BASE_URL = "https://fibre-crm.s3.eu-west-2.amazonaws.com/";
    return S3_BASE_URL + s3Url;
  };

  const getFileIcon = (contentType, size = 24) => {
    if (contentType?.startsWith("image/"))
      return <ImageIcon size={size} className="text-blue-400" />;
    if (contentType?.startsWith("video/"))
      return <Video size={size} className="text-purple-400" />;
    if (contentType?.startsWith("audio/"))
      return <Music size={size} className="text-green-400" />;
    if (contentType?.includes("pdf"))
      return <FileText size={size} className="text-red-400" />;
    if (contentType?.includes("word") || contentType?.includes("document"))
      return <FileText size={size} className="text-blue-400" />;
    if (contentType?.includes("sheet") || contentType?.includes("excel"))
      return <FileText size={size} className="text-green-400" />;
    if (
      contentType?.includes("presentation") ||
      contentType?.includes("powerpoint")
    )
      return <FileText size={size} className="text-orange-400" />;
    return <FileIcon size={size} className="text-gray-400" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getParentTypeIcon = (parentType) => {
    switch (parentType?.toLowerCase()) {
      case "account":
        return <Building2 size={16} className="text-blue-400" />;
      case "contact":
        return <UserCircle size={16} className="text-green-400" />;
      case "lead":
        return <Users size={16} className="text-yellow-400" />;
      case "opportunity":
        return <Briefcase size={16} className="text-purple-400" />;
      case "case":
        return <FileQuestion size={16} className="text-red-400" />;
      case "project":
        return <FolderKanban size={16} className="text-indigo-400" />;
      default:
        return <FileIcon size={16} className="text-gray-400" />;
    }
  };

  const parentTypeOptions = [
    { value: "all", label: "All Types" },
    { value: "Account", label: "Accounts" },
    { value: "Contact", label: "Contacts" },
    { value: "Lead", label: "Leads" },
    { value: "Opportunity", label: "Opportunities" },
    { value: "Case", label: "Cases" },
    { value: "Project", label: "Projects" },
  ];

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 p-6 ${isDarkMode ? "bg-[#0A0C10] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1
              className={`text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Salesforce Attachments
            </h1>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Manage attachments imported from Salesforce
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div
              className={`rounded-lg p-4 border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <div
                className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Total Attachments
              </div>
              <div
                className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {stats.total || 0}
              </div>
            </div>
            <div
              className={`rounded-lg p-4 border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <div
                className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Migrated
              </div>
              <div className="text-2xl font-bold text-green-400">
                {stats.migrated || 0}
              </div>
            </div>
            <div
              className={`rounded-lg p-4 border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <div
                className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Not Migrated
              </div>
              <div className="text-2xl font-bold text-yellow-400">
                {stats.notMigrated || 0}
              </div>
            </div>
            <div
              className={`rounded-lg p-4 border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <div
                className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Parent Types
              </div>
              <div
                className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {stats.byParentType?.length || 0}
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
              size={20}
            />
            <input
              type="text"
              placeholder="Search attachments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none transition-colors ${isDarkMode ? "bg-[#13161B] border border-gray-700 focus:border-blue-500" : "bg-white border border-gray-300 focus:border-blue-400"}`}
            />
          </div>

          <select
            value={filterParentType}
            onChange={(e) => setFilterParentType(e.target.value)}
            className={`px-4 py-2 rounded-lg focus:outline-none transition-colors ${isDarkMode ? "bg-[#13161B] border border-gray-700 focus:border-blue-500" : "bg-white border border-gray-300 focus:border-blue-400"}`}
          >
            {parentTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
              className={`px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-[#13161B] border border-gray-700 hover:bg-gray-800" : "bg-white border border-gray-200 hover:bg-gray-50 shadow-sm"}`}
            >
              {viewMode === "grid" ? <List size={20} /> : <Grid size={20} />}
            </button>

            <button
              onClick={() =>
                setSortOrder(sortOrder === "DESC" ? "ASC" : "DESC")
              }
              className={`px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-[#13161B] border border-gray-700 hover:bg-gray-800" : "bg-white border border-gray-200 hover:bg-gray-50 shadow-sm"}`}
            >
              {sortOrder === "DESC" ? (
                <SortDesc size={20} />
              ) : (
                <SortAsc size={20} />
              )}
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedAttachments.size > 0 && (
          <div
            className={`mt-4 flex items-center gap-4 p-4 rounded-lg ${isDarkMode ? "bg-blue-500/10 border border-blue-500/30" : "bg-blue-50 border border-blue-200"}`}
          >
            <span
              className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              {selectedAttachments.size} attachment(s) selected
            </span>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-sm font-medium transition-colors text-white"
            >
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedAttachments(new Set())}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className={`rounded-lg border transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
      >
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader
              className={`animate-spin ${isDarkMode ? "text-white" : "text-gray-900"}`}
              size={32}
            />
          </div>
        ) : attachments.length === 0 ? (
          <div
            className={`flex flex-col items-center justify-center h-64 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <FileIcon size={48} className="mb-4" />
            <p className="text-lg">No attachments found</p>
            <p className="text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            {viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-6">
                {attachments.map((attachment) => (
                  <AttachmentGridCard
                    key={attachment.id}
                    attachment={attachment}
                    isSelected={selectedAttachments.has(attachment.id)}
                    onSelect={() => toggleAttachmentSelection(attachment.id)}
                    onDelete={() => handleDeleteAttachment(attachment.id)}
                    getFileIcon={getFileIcon}
                    formatFileSize={formatFileSize}
                    getFullFileUrl={getFullFileUrl}
                    getParentTypeIcon={getParentTypeIcon}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead
                    className={`border-b ${isDarkMode ? "bg-[#0A0C10] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                  >
                    <tr>
                      <th className="p-3 text-left">
                        <input
                          type="checkbox"
                          checked={
                            attachments.length > 0 &&
                            selectedAttachments.size === attachments.length
                          }
                          onChange={selectAllAttachments}
                          className="rounded"
                        />
                      </th>
                      <th
                        className={`p-3 text-left text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Name
                      </th>
                      <th
                        className={`p-3 text-left text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Parent
                      </th>
                      <th
                        className={`p-3 text-left text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Type
                      </th>
                      <th
                        className={`p-3 text-left text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Size
                      </th>
                      <th
                        className={`p-3 text-left text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Created
                      </th>
                      <th
                        className={`p-3 text-left text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Owner
                      </th>
                      <th
                        className={`p-3 text-left text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Status
                      </th>
                      <th
                        className={`p-3 text-left text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {attachments.map((attachment) => (
                      <AttachmentListRow
                        key={attachment.id}
                        attachment={attachment}
                        isSelected={selectedAttachments.has(attachment.id)}
                        onSelect={() =>
                          toggleAttachmentSelection(attachment.id)
                        }
                        onDelete={() => handleDeleteAttachment(attachment.id)}
                        getFileIcon={getFileIcon}
                        formatFileSize={formatFileSize}
                        getFullFileUrl={getFullFileUrl}
                        getParentTypeIcon={getParentTypeIcon}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                className={`flex items-center justify-between p-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <div
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Showing {(page - 1) * limit + 1} to{" "}
                  {Math.min(page * limit, totalAttachments)} of{" "}
                  {totalAttachments} attachments
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
                  >
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`px-3 py-1 rounded ${page === pageNum ? (isDarkMode ? "bg-blue-500 text-white" : "bg-blue-600 text-white") : isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Attachment Grid Card Component
const AttachmentGridCard = ({
  attachment,
  isSelected,
  onSelect,
  onDelete,
  getFileIcon,
  formatFileSize,
  getFullFileUrl,
  getParentTypeIcon,
  isDarkMode,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const fullFileUrl = getFullFileUrl(attachment.s3Url || attachment.body);

  return (
    <div
      className={`relative group rounded-lg border transition-all ${
        isSelected
          ? "border-blue-500 ring-2 ring-blue-500/20"
          : isDarkMode
            ? "bg-[#0A0C10] border-gray-700 hover:border-gray-600"
            : "bg-white border-gray-200 hover:border-gray-300 shadow-sm"
      }`}
    >
      <div className="absolute top-2 left-2 z-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="w-5 h-5 rounded"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {attachment.isMigrated && (
        <div className="absolute top-2 right-2 z-10">
          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
            Migrated
          </span>
        </div>
      )}

      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className={`p-1.5 rounded transition-colors opacity-0 group-hover:opacity-100 ${isDarkMode ? "bg-gray-800/80 hover:bg-gray-700" : "bg-white/80 hover:bg-gray-100 border border-gray-200"}`}
        >
          <MoreVertical size={16} />
        </button>

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
            />
            <div
              className={`absolute right-0 top-8 w-48 rounded-lg shadow-xl z-20 border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              {fullFileUrl && (
                <a
                  href={fullFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
                  onClick={() => setShowMenu(false)}
                >
                  <ExternalLink size={14} />
                  Open
                </a>
              )}
              {fullFileUrl && (
                <a
                  href={fullFileUrl}
                  download
                  className={`flex items-center gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
                  onClick={() => setShowMenu(false)}
                >
                  <Download size={14} />
                  Download
                </a>
              )}
              <div
                className={`my-1 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
              ></div>
              <button
                onClick={() => {
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      <div className="aspect-square flex items-center justify-center p-6">
        {attachment.contentType?.startsWith("image/") && fullFileUrl ? (
          <img
            src={fullFileUrl}
            alt={attachment.name}
            className="w-full h-full object-cover rounded"
          />
        ) : (
          getFileIcon(attachment.contentType, 48)
        )}
      </div>

      <div
        className={`p-3 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <div
          className={`font-medium text-sm truncate mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          title={attachment.name}
        >
          {attachment.name}
        </div>
        <div
          className={`flex items-center justify-between text-xs mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          <span>{formatFileSize(attachment.bodyLength)}</span>
        </div>
        {attachment.parentEntity && (
          <div
            className={`flex items-center gap-1 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {getParentTypeIcon(attachment.parentType)}
            <span className="truncate">{attachment.parentEntity.name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Attachment List Row Component
const AttachmentListRow = ({
  attachment,
  isSelected,
  onSelect,
  onDelete,
  getFileIcon,
  formatFileSize,
  getFullFileUrl,
  getParentTypeIcon,
  isDarkMode,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const fullFileUrl = getFullFileUrl(attachment.s3Url || attachment.body);

  return (
    <tr
      className={`border-b transition-colors ${isDarkMode ? "border-gray-700/50 hover:bg-gray-800/30" : "border-gray-100 hover:bg-gray-50"}`}
    >
      <td className="p-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="rounded"
        />
      </td>
      <td className="p-3">
        <div className="flex items-center gap-3">
          {getFileIcon(attachment.contentType, 20)}
          <div className="flex-1 min-w-0">
            <div
              className={`font-medium text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
              title={attachment.name}
            >
              {attachment.name}
            </div>
          </div>
        </div>
      </td>
      <td className="p-3">
        {attachment.parentEntity ? (
          <div className="flex items-center gap-2 text-sm">
            {getParentTypeIcon(attachment.parentType)}
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {attachment.parentEntity.name}
              </div>
              <div
                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                {attachment.parentType}
              </div>
            </div>
          </div>
        ) : (
          <span
            className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {attachment.parentType}
          </span>
        )}
      </td>
      <td
        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {attachment.contentType || "—"}
      </td>
      <td
        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {formatFileSize(attachment.bodyLength)}
      </td>
      <td
        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {new Date(attachment.createdAt).toLocaleDateString()}
      </td>
      <td
        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {attachment.owner?.name || "—"}
      </td>
      <td className="p-3">
        {attachment.isMigrated ? (
          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
            Migrated
          </span>
        ) : (
          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full border border-yellow-500/30">
            Pending
          </span>
        )}
      </td>
      <td className="p-3">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className={`p-1.5 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
          >
            <MoreVertical size={16} />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div
                className={`absolute right-0 top-8 w-48 rounded-lg shadow-xl z-20 border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                {fullFileUrl && (
                  <a
                    href={fullFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
                    onClick={() => setShowMenu(false)}
                  >
                    <ExternalLink size={14} />
                    Open
                  </a>
                )}
                {fullFileUrl && (
                  <a
                    href={fullFileUrl}
                    download
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
                    onClick={() => setShowMenu(false)}
                  >
                    <Download size={14} />
                    Download
                  </a>
                )}
                <div
                  className={`my-1 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                ></div>
                <button
                  onClick={() => {
                    onDelete();
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default SalesforceAttachments;
